import { cannotDrive, driverBudgets } from '@racedash/shared'
import { Tile } from '../Tile'
import { useDriverStints } from '../useDriverStints'
import type { WidgetProps } from './props'

/**
 * How much of the race each driver has left.
 *
 * Time where there is a cumulative cap, stints where there is a balance rule,
 * and both where both apply. The most useful line during a long race is not who
 * is quickest but who is nearly out.
 *
 * When a count rule applies and any stint has no driver against it, this shows
 * no counts at all and says how many are missing. A share computed from half
 * the data reads as "you are fine" right up until it is wrong, and this is
 * exactly the number somebody would act on at four in the morning.
 */
export default function DriverBudget({ car, data, now, context }: WidgetProps<'driverBudget'>) {
  const { stints, at } = useDriverStints(car, data, context, now)
  const regime = context.plan?.regime
  const report = driverBudgets(stints, regime, context.drivers, at)

  if (!regime) return <Tile label="Drivers">{blank('No race plan')}</Tile>
  if (report.drivers.length === 0) return <Tile label="Drivers">{blank('No drivers yet')}</Tile>

  return (
    <Tile label="Drivers">
      <div className="budget">
        {report.incomplete && (
          <div className="budget-warn">
            {report.unattributed} stint{report.unattributed === 1 ? '' : 's'} with no driver — stint
            counts cannot be worked out until every one has been named.
          </div>
        )}
        <table className="budget-table">
          <tbody>
            {report.drivers.map((d) => {
              const blocked = cannotDrive(d, report, regime, stints)
              return (
                <tr key={d.driverId} data-current={d.current || undefined} data-done={d.complete || undefined}>
                  <td className="budget-name">{d.name ?? d.driverId}</td>
                  <td className="budget-num">{fmt(d.usedMs)}</td>
                  <td className="budget-num">
                    {d.remainingMs != null ? `${fmt(d.remainingMs)} left` : ''}
                  </td>
                  <td className="budget-num">
                    {!report.incomplete && d.stintCeiling != null
                      ? `${d.stints}/${d.stintCeiling}`
                      : d.stints || ''}
                  </td>
                  <td className="budget-note">{blocked ?? (d.current ? 'in the car' : '')}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Tile>
  )
}

function blank(reason: string) {
  return <div className="budget-blank">{reason}</div>
}

function fmt(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 60_000))
  const h = Math.floor(total / 60)
  return h > 0 ? `${h}h${String(total % 60).padStart(2, '0')}` : `${total}m`
}
